// domain/constants/unitBasis.js

/**
 * How a billable code is MEASURED — per 15 minutes, per hour, per session, per day, per diem.
 *
 * THE BASIS, NEVER THE COUNT, and the distinction is the whole reason the concept has a name. The
 * basis says what one unit IS; the count says how many were delivered. Rounding, the 8-minute rule
 * and pooling all compute the count at claim time and are a different concern entirely — a rule that
 * reaches for this to decide "how many units" has already gone wrong.
 *
 * "BASIS" RATHER THAN "TYPE" because it is the claim's own word: 837P calls SV103 the "Unit or Basis
 * for Measurement Code", and a code entity exists to live in claims-truth.
 *
 * A CODE-TRUTH, which is why it lives on the service code and nowhere else. Two clinical services can
 * route to one code, and that code has ONE basis whichever of them got there — a copy on the service
 * is what would let them disagree.
 *
 * THE VALUES ARE A CROSS-REPO CONTRACT. The billing projector reads a basis and holds unless the
 * value is one of these exact strings; the frontend cannot import from `functions/`, so its copy is
 * a deliberate duplicate. Do not add, remove or rename a VALUE without making the identical change
 * there, or a value one side accepts will hold on the other.
 *
 * @domainObject value-object
 * @invariant Extension is additive. A new basis is a new member, never a reshape of codes already
 *   stored under an existing one.
 * @invariant Names how a unit is measured, never how many were delivered. A member that answers
 *   "how many" belongs to unit computation at claim time, which is not this vocabulary.
 * @typedef {'per_15min' | 'per_hour' | 'per_session' | 'per_day' | 'per_diem'} UnitBasis
 */
export const UNIT_BASIS = {
  PER_15MIN: 'per_15min',
  PER_HOUR: 'per_hour',
  PER_SESSION: 'per_session',
  PER_DAY: 'per_day',
  PER_DIEM: 'per_diem',
};

/**
 * Display order for a basis picker — shortest measured span first, so the timed bases group.
 *
 * A PRESENTATION ORDER held in the domain because it is a property of the vocabulary rather than of
 * any one screen: two pickers ordering these differently would make the same list look like two
 * different sets. It carries no billing meaning and nothing resolves through it.
 */
export const UNIT_BASIS_ORDER = [
  UNIT_BASIS.PER_15MIN,
  UNIT_BASIS.PER_HOUR,
  UNIT_BASIS.PER_SESSION,
  UNIT_BASIS.PER_DAY,
  UNIT_BASIS.PER_DIEM,
];

export const UNIT_BASIS_VALUES = Object.values(UNIT_BASIS);

export const isUnitBasis = (value) => UNIT_BASIS_VALUES.includes(value);
